import { useState, useMemo, useContext, memo } from "react";
import LazyLoad from "react-lazy-load";
import CollectionButton from "./CollectionButton";
import ToolTip from "./ToolTip";
import { MaterialContext } from "../context/MaterialContext";
import { UserContext } from "../context/UserContext";
import { nikeLogo } from "./Icons";

import swatchStyle from "../styles/swatch.module.scss";

import MaterialModal from "./MaterialModal";

const Swatch = memo(function Swatch({ id }) {
  const { materialById, nikeDataBySlug, changeSearch, ROOT_SERVER } =
    useContext(MaterialContext);
  const { userInformation, media } = useContext(UserContext);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageNumber, setImageNumber] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);

  const showLogo =
    userInformation.vendor === "asiatan" || userInformation.vendor === "nike";

  const showCount =
    userInformation.vendor === "asiatan" ||
    userInformation.vendor === "tectuff";

  const materialData = useMemo(() => {
    if (!id) {
      return null;
    }

    return materialById?.get(id) ?? null;
  }, [id, materialById]);

  const brandData = useMemo(() => {
    if (!materialData?.slug) {
      return [];
    }

    const match = nikeDataBySlug?.get(materialData.slug);
    return match ? [match] : [];
  }, [materialData?.slug, nikeDataBySlug]);

  const isNike = brandData.length > 0;

  const viewing = useMemo(() => {
    if (!materialData) {
      return "";
    }

    const materialImages = Array.isArray(materialData.images)
      ? materialData.images
      : [];
    const safeImage = materialImages[imageNumber] || materialImages[0] || "";

    return safeImage
      ? `${ROOT_SERVER}${materialData.vendor}/${materialData.slug}/swatch/${safeImage}`
      : "";
  }, [materialData, imageNumber, ROOT_SERVER]);

  const handleChangeImage = (i) => {
    setImageLoaded(false);
    setImageNumber(i);
  };
  const handleImageLoaded = () => {
    setImageLoaded(true);
  };
  const handleShowModal = () => {
    setShowModal(true);
  };

  if (!materialData) {
    return null;
  }

  const { title, description, vendor, slug, images, view_count } = materialData;

  return (
    <>
      {showModal && (
        <MaterialModal
          id={id}
          showModal={showModal}
          setShowModal={setShowModal}
          brandData={brandData}
          userInformation={userInformation}
          media={media}
          imageNum={imageNumber}
        />
      )}
      <div className={swatchStyle.swatch} data-id={id}>
        <div className={swatchStyle.swatchImageFrame}>
          <LazyLoad offset={200} threshold={1}>
            <img
              onClick={handleShowModal}
              className={
                imageLoaded
                  ? `${swatchStyle.imageBlur} ${swatchStyle.loaded}`
                  : `${swatchStyle.imageBlur}`
              }
              src={viewing}
              alt={slug}
              onLoad={handleImageLoaded}
            />
          </LazyLoad>
          {images?.length >= 2 && (
            <div className={swatchStyle.imageSelector}>
              {images?.map((image, i) => (
                <span
                  key={image}
                  onClick={() => handleChangeImage(i)}
                  style={
                    imageNumber === i
                      ? { background: "transparent" }
                      : { background: "#FEFEFE90" }
                  }
                />
              ))}
            </div>
          )}
        </div>
        <div className={swatchStyle.vendor}>
          <p
            className={swatchStyle.vendorLabel}
            onClick={() => changeSearch(vendor)}
          >
            {vendor}
          </p>
          {showCount ? <p>views: {view_count}</p> : null}
          {isNike && showLogo ? (
            <div
              className={swatchStyle.logo}
              onMouseOver={() => setShowToolTip(true)}
              onMouseLeave={() => setShowToolTip(false)}
            >
              {nikeLogo}
              <ToolTip
                brandData={brandData}
                setShowToolTip={setShowToolTip}
                showToolTip={showToolTip}
                brand={userInformation.vendor}
              />
            </div>
          ) : (
            false
          )}
        </div>
        <div className={swatchStyle.collectionButton}>
          <CollectionButton materialId={id} />
        </div>

        <div className={swatchStyle.information}>
          {!imageLoaded && <div className={swatchStyle.loadingBar} />}
          <h3>{title}</h3>
          <h4>{description}</h4>
        </div>
      </div>
    </>
  );
});

export default Swatch;
